import {FeaturesEnum, HealthGuideServicesTypeEnum, QueryParamsEnum} from 'presentation/constants';
import {PlanTypeEnum} from 'presentation/constants/bookingTypesEnum';
import {useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useFilteredHealthProviders} from 'presentation/modules/guide/health/hooks/useFilteredHealthProviders';
import {UseHealthProvidersQueryParams} from 'presentation/modules/guide/health/queries/useHealthProvidersQuery/types';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';
import {useGuideNavigation} from 'presentation/modules/guide/shared/hooks/useGuideNavigation/useGuideNavigation';
import {useGuideQueryParams} from 'presentation/modules/guide/shared/hooks/useGuideQueryParams';
import {ppoNetworkTypeEnum} from 'presentation/modules/guide/shared/store/guideStore/types';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {useAccountInformation, useMediaQueries, useQueryParams} from 'presentation/pages/private';
import {translations} from 'presentation/translations-new';
import {useEffect, useState} from 'react';

const {
	titleNetworkProviderHealth,
	titleTelemedicineNetwork,
	titleOwnNetwork,
	titleAccreditedNetwork,
	titleNotreDameHapvidaNetwork,
} = translations['pt-br'].privateFlow.guideFlow;

const {failToLoadTitle, failToLoadDescription, loading} = translations['pt-br'].commons;

export function useHealthGuide() {
	const {isMobile} = useMediaQueries();
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {deleteAllQueryParams} = useGuideQueryParams();
	const {isEarlyAccess, beneficiary} = useAccountInformation();

	const [isEarlyAccessModalOpen, setIsEarlyAccessModalOpen] = useState(false);

	const {isEnabled: showProvidersV3Component} = useFeatureFlag(
		FeaturesEnum.PORTAL_MOD_GUIDE_FLOW_MEDICAL_GUIDE_GET_PROVIDERS_V3,
	);

	useGuideNavigation({type: PlanTypeEnum.HEALTH});

	const ppoNetworkTypeState = usePpoNetworkStore(state => state.ppoNetworkType);

	const {isGndiSaoPaulo, isNotredameSaude} = useHostName();

	const {
		ufCode,
		cityName,
		coordinates,
		serviceType,
		specialtyName,
		cd_procedimento,
		networkCode,
		step: currentStepState,
		distance,
	} = SEARCH_PARAMS;

	const getNetworkInfo = () => {
		if (isNotredameSaude) {
			if (ppoNetworkTypeState === ppoNetworkTypeEnum.SELF_NETWORK) {
				return {
					tpRede: 1,
					title: titleNotreDameHapvidaNetwork,
				};
			}

			if (ppoNetworkTypeState === ppoNetworkTypeEnum.ACCREDITED_NETWORK) {
				return {
					tpRede: 2,
					title: titleAccreditedNetwork,
				};
			}
		}

		return {
			tpRede: networkCode ? Number(networkCode) : undefined,
			title: titleNetworkProviderHealth,
		};
	};

	const healthParams: UseHealthProvidersQueryParams = {
		type: PlanTypeEnum.HEALTH,
		cdUf: ufCode || '',
		nmCidade: cityName || '',
		coordenadas: coordinates || undefined,
		dsTipo: serviceType,
		dsEspecialidade: specialtyName || '',
		cd_procedimento: cd_procedimento || undefined,
		tpRede: getNetworkInfo().tpRede,
		retornoAgrupado: isGndiSaoPaulo,
		distancia: distance || '25',
	};

	const {
		data: filteredProviders,
		allProviders,
		isFetching,
		isError: providersError,
		refetchProvidersQuery,
	} = useFilteredHealthProviders({
		...healthParams,
	});

	const isError = providersError;

	const isTelemedicinaEmptyOrFiltered =
		filteredProviders?.telemedicina.length === 0 ||
		(serviceType !== HealthGuideServicesTypeEnum.OUTPATIENT &&
			filteredProviders?.telemedicina.length === 1);

	const isFilteredProvidersEmpty =
		filteredProviders?.redeCredenciada.length === 0 &&
		filteredProviders?.redePropria.length === 0 &&
		isTelemedicinaEmptyOrFiltered;

	const shouldRenderEmptyState =
		!isError && (allProviders?.length === 0 || isFilteredProvidersEmpty);

	const EMPTY_GROUPED_PROVIDERS = {telemedicina: [], redePropria: [], redeCredenciada: []};

	const {telemedicina, redePropria, redeCredenciada} =
		filteredProviders || EMPTY_GROUPED_PROVIDERS;

	const networkList = {accredited: redeCredenciada, own: redePropria, telemedicine: telemedicina};

	const networkTitles = {
		accreditedNetwork: titleAccreditedNetwork,
		ownNetwork: isNotredameSaude ? titleNotreDameHapvidaNetwork : titleOwnNetwork,
		telemedicineNetwork: titleTelemedicineNetwork,
	};

	const onSearchResults = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.STEP, GuideStep.RESULTS);
			return prev;
		});
	};

	useEffect(() => {
		if (!currentStepState) {
			deleteAllQueryParams();
		}
	}, []);

	useEffect(() => {
		if (isEarlyAccess && !currentStepState) {
			setIsEarlyAccessModalOpen(true);
		}
	}, [isEarlyAccess, currentStepState]);

	return {
		shouldRenderEmptyState,
		isError,
		isFetching,
		networkList,
		networkTitles,
		specialtyName,
		failToLoadTitle,
		failToLoadDescription,
		loading,
		titleNetworkProviderHealth,
		refetchProvidersQuery,
		isMobile,
		currentStepState,
		allProviders,
		providersError,
		setSearchParams,
		isEarlyAccessModalOpen,
		setIsEarlyAccessModalOpen,
		beneficiary,
		showProvidersV3Component,
		onSearchResults,
		getNetworkInfo,
	};
}
